/**
 * User Statistics Service
 * 
 * Manages user travel statistics in Firestore
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  getCountriesFromTrips,
  getCitiesFromTrips,
  calculateTotalTripDays,
  getContinentsFromCountries,
  calculateTravelStreak,
  extractLandmarkVisits,
  getTripsEligibleForTravelStats,
} from './gamification';
import type { Trip } from './trips';
import type { ItineraryItem } from './itinerary';

export type CountryVisit = {
  country: string;
  firstVisit: Date;
  visitCount: number;
  years: number[];
};

export type CityVisit = {
  city: string;
  country: string;
  firstVisit: Date;
  visitCount: number;
};

export type LandmarkVisit = {
  landmarkId: string;
  name: string;
  location: string;
  visitDate: Date;
};

export type UserStats = {
  userId: string;
  countriesVisited: CountryVisit[];
  citiesVisited: CityVisit[];
  landmarksVisited: LandmarkVisit[];
  totalTripDays: number;
  continentsVisited: string[];
  currentStreak: number;
  longestStreak: number;
  lastUpdated: Date;
};

type FirestoreCountryVisit = {
  country: string;
  firstVisit: Timestamp | Date;
  visitCount: number;
  years: number[];
};

type FirestoreCityVisit = {
  city: string;
  country: string;
  firstVisit: Timestamp | Date;
  visitCount: number;
};

type FirestoreLandmarkVisit = {
  landmarkId: string;
  name: string;
  location: string;
  visitDate: Timestamp | Date;
};

type FirestoreUserStats = {
  userId: string;
  countriesVisited: FirestoreCountryVisit[];
  citiesVisited: FirestoreCityVisit[];
  landmarksVisited: FirestoreLandmarkVisit[];
  totalTripDays: number;
  continentsVisited: string[];
  currentStreak: number;
  longestStreak: number;
  lastUpdated: Timestamp | Date;
};

/**
 * Convert Firestore timestamp to Date
 */
function convertTimestamp(date: Timestamp | Date | string): Date {
  if (date instanceof Timestamp) {
    return date.toDate();
  }
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string') {
    return new Date(date);
  }
  return new Date();
}

/**
 * Convert Firestore user stats to app UserStats type
 */
function convertFirestoreUserStats(data: FirestoreUserStats): UserStats {
  return {
    userId: data.userId,
    countriesVisited: data.countriesVisited.map(c => ({
      country: c.country,
      firstVisit: convertTimestamp(c.firstVisit),
      visitCount: c.visitCount,
      years: c.years,
    })),
    citiesVisited: data.citiesVisited.map(c => ({
      city: c.city,
      country: c.country,
      firstVisit: convertTimestamp(c.firstVisit),
      visitCount: c.visitCount,
    })),
    landmarksVisited: data.landmarksVisited.map(l => ({
      landmarkId: l.landmarkId,
      name: l.name,
      location: l.location,
      visitDate: convertTimestamp(l.visitDate),
    })),
    totalTripDays: data.totalTripDays,
    continentsVisited: data.continentsVisited,
    currentStreak: data.currentStreak,
    longestStreak: data.longestStreak,
    lastUpdated: convertTimestamp(data.lastUpdated),
  };
}

/**
 * Fetch user statistics
 */
export async function getUserStats(userId: string): Promise<UserStats | null> {
  try {
    const statsRef = doc(db, 'userStats', userId);
    const snapshot = await getDoc(statsRef);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    const data = snapshot.data() as FirestoreUserStats;
    return convertFirestoreUserStats(data);
  } catch (error) {
    console.error('[userStats] Error fetching user stats:', error);
    return null;
  }
}

/**
 * Calculate and update user statistics from trips
 */
export async function updateUserStatsFromTrips(
  userId: string,
  trips: Trip[],
  itineraryItems?: ItineraryItem[]
): Promise<UserStats> {
  const eligibleTrips = getTripsEligibleForTravelStats(trips);
  const countries = getCountriesFromTrips(eligibleTrips);
  const cities = getCitiesFromTrips(eligibleTrips);
  const totalTripDays = calculateTotalTripDays(eligibleTrips);
  const continents = getContinentsFromCountries(countries);
  const { currentStreak, longestStreak } = calculateTravelStreak(eligibleTrips);
  
  // Extract landmark visits from itinerary items if provided
  let landmarks: LandmarkVisit[] = [];
  if (itineraryItems) {
    landmarks = extractLandmarkVisits(itineraryItems);
  }
  
  // Get existing stats to merge landmark visits
  const existingStats = await getUserStats(userId);
  if (existingStats) {
    // Merge landmark visits (avoid duplicates)
    const existingLandmarkIds = new Set(existingStats.landmarksVisited.map(l => l.landmarkId));
    const newLandmarks = landmarks.filter(l => !existingLandmarkIds.has(l.landmarkId));
    landmarks = [...existingStats.landmarksVisited, ...newLandmarks];
  }
  
  const stats: UserStats = {
    userId,
    countriesVisited: countries,
    citiesVisited: cities,
    landmarksVisited: landmarks,
    totalTripDays,
    continentsVisited: continents,
    currentStreak,
    longestStreak,
    lastUpdated: new Date(),
  };
  
  // Save to Firestore
  await saveUserStats(stats);
  
  return stats;
}

/**
 * Save user statistics to Firestore
 */
export async function saveUserStats(stats: UserStats): Promise<void> {
  try {
    const statsRef = doc(db, 'userStats', stats.userId);
    
    const firestoreStats: FirestoreUserStats = {
      userId: stats.userId,
      countriesVisited: stats.countriesVisited.map(c => ({
        country: c.country,
        firstVisit: Timestamp.fromDate(c.firstVisit),
        visitCount: c.visitCount,
        years: c.years,
      })),
      citiesVisited: stats.citiesVisited.map(c => ({
        city: c.city,
        country: c.country,
        firstVisit: Timestamp.fromDate(c.firstVisit),
        visitCount: c.visitCount,
      })),
      landmarksVisited: stats.landmarksVisited.map(l => ({
        landmarkId: l.landmarkId,
        name: l.name,
        location: l.location,
        visitDate: Timestamp.fromDate(l.visitDate),
      })),
      totalTripDays: stats.totalTripDays,
      continentsVisited: stats.continentsVisited,
      currentStreak: stats.currentStreak,
      longestStreak: stats.longestStreak,
      lastUpdated: serverTimestamp() as Timestamp,
    };
    
    await setDoc(statsRef, firestoreStats, { merge: true });
  } catch (error) {
    console.error('[userStats] Error saving user stats:', error);
    throw error;
  }
}

/**
 * Track a landmark visit
 */
export async function trackLandmarkVisit(
  userId: string,
  landmarkId: string,
  name: string,
  location: string,
  visitDate: Date = new Date()
): Promise<void> {
  try {
    const stats = await getUserStats(userId);
    
    if (!stats) {
      // Create initial stats if they don't exist
      const newStats: UserStats = {
        userId,
        countriesVisited: [],
        citiesVisited: [],
        landmarksVisited: [{
          landmarkId,
          name,
          location,
          visitDate,
        }],
        totalTripDays: 0,
        continentsVisited: [],
        currentStreak: 0,
        longestStreak: 0,
        lastUpdated: new Date(),
      };
      await saveUserStats(newStats);
      return;
    }
    
    // Check if landmark already visited
    const existingIndex = stats.landmarksVisited.findIndex(
      l => l.landmarkId === landmarkId
    );
    
    if (existingIndex >= 0) {
      // Update existing visit date if this is a more recent visit
      if (visitDate > stats.landmarksVisited[existingIndex].visitDate) {
        stats.landmarksVisited[existingIndex].visitDate = visitDate;
      }
    } else {
      // Add new landmark visit
      stats.landmarksVisited.push({
        landmarkId,
        name,
        location,
        visitDate,
      });
    }
    
    stats.lastUpdated = new Date();
    await saveUserStats(stats);
  } catch (error) {
    console.error('[userStats] Error tracking landmark visit:', error);
    throw error;
  }
}

/**
 * Update user stats when a trip is completed
 */
export async function updateUserStatsOnTripCompletion(
  userId: string,
  trip: Trip,
  itineraryItems?: ItineraryItem[]
): Promise<UserStats | null> {
  try {
    // Fetch all user trips to recalculate stats
    const { fetchUserTrips } = await import('./trips');
    const { trips: allTrips } = await fetchUserTrips(userId);
    
    // Update stats with all trips
    return await updateUserStatsFromTrips(userId, allTrips, itineraryItems);
  } catch (error) {
    console.error('[userStats] Error updating stats on trip completion:', error);
    return null;
  }
}

/**
 * Get yearly statistics
 */
export async function getYearlyUserStats(
  userId: string,
  year: number
): Promise<{
  countries: number;
  cities: number;
  tripDays: number;
  trips: number;
} | null> {
  try {
    const { fetchUserTrips } = await import('./trips');
    const { trips } = await fetchUserTrips(userId);
    
    const { getYearlyStats } = await import('./gamification');
    return getYearlyStats(trips, year);
  } catch (error) {
    console.error('[userStats] Error getting yearly stats:', error);
    return null;
  }
}

